import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { AaiSkill, ControlEvent } from '../../types/aai';
import { Action, Card, Chip, EmptyState, FactList, Kicker, Loading, RailCard, RecordModal, RecordPage, StatusChip, useRecordBack } from '../../components/aa';
import { relativeTime } from '../partner/format';
import { audienceOf } from './WorkRows';
import { sentLine } from './ControlDrawers';
import { SkillRunBody } from '../employee/SkillRun';

/*
 * A company capability, in business terms.
 *
 * A Skill is the implementation; a Quick Win is the business outcome it
 * delivers. The Owner reads who it is for, the human check, the cost and where
 * the work happens, sees the recorded example, runs it here the way an
 * employee does, and can send feedback or ask for a customization. Both are recorded requests: they notify the Quick Win owner
 * and the AI Champion and land in Activity. The released version stays
 * unchanged until the owner acts. Skill configuration is not part of this view.
 */

type Mode = 'read' | 'feedback' | 'customization';

const FIELD = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink';

const FORM: Record<Exclude<Mode, 'read'>, { kind: string; title: string; hint: string; placeholder: string; action: string; done: string }> = {
  feedback: {
    kind: 'capability_feedback',
    title: 'Send feedback',
    hint: 'Tell the owner what happened. They read it against the released version.',
    placeholder: 'The draft rota missed the Sunday trading hours twice this month.',
    action: 'Send feedback',
    done: 'Feedback sent.',
  },
  customization: {
    kind: 'customization_request',
    title: 'Request customization',
    hint: 'Say what should change. The released version stays unchanged during review.',
    placeholder: 'Add the trading calendar as a required input.',
    action: 'Send request',
    done: 'Customization requested.',
  },
};

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="border-b border-r border-line px-5 py-4 last:border-b-0 sm:[&:nth-child(2n)]:border-r-0 sm:[&:nth-last-child(-n+2)]:border-b-0">
    <div className="text-[12px] text-ink-dim">{label}</div>
    <div className="mt-1 text-[14px] font-semibold text-ink">{value}</div>
  </div>
);

export const CapabilityDrawer: React.FC<{
  skill: AaiSkill;
  onClose: () => void;
  /** Render as its own page with this back link, instead of a dialog. */
  page?: { backLabel: string };
}> = ({ skill, onClose, page }) => {
  const [mode, setMode] = useState<Mode>('read');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ControlEvent | null>(null);
  const [showExample, setShowExample] = useState(false);
  const [running, setRunning] = useState(false);

  const inAudience = skill.my_access === 'open' || skill.my_access === 'granted' || skill.access_level === 'open';
  const hasExample = Boolean(skill.example_input || skill.example_output);
  // The server runs a skill only for someone who can reach it, so the button
  // follows the same rule rather than offering a run that is then refused.
  const canRun = skill.my_access === 'open' || skill.my_access === 'granted';

  const send = async () => {
    if (mode === 'read') return;
    const form = FORM[mode];
    if (!note.trim()) {
      toast.error('Say it in a sentence. The owner reads it.');
      return;
    }
    setBusy(true);
    try {
      const res = await aaiApi.recordControlEvent({
        kind: form.kind,
        subject: skill.name,
        status: 'requested',
        payload: { skill_id: skill.id, note: note.trim(), owner_email: skill.created_by || undefined },
      });
      setDone(res.data);
      toast.success(`${form.done} ${sentLine(res.data)}`);
      setMode('read');
      setNote('');
    } catch {
      toast.error('That did not send.');
    } finally {
      setBusy(false);
    }
  };

  const facts = [
    { label: 'Who it is for', value: audienceOf(skill) },
    { label: 'Owner', value: skill.created_by || 'Not recorded' },
    { label: 'Human check', value: 'People review every output' },
    { label: 'Cost', value: 'Included in your plan' },
    { label: 'Where work happens', value: 'Focused panel' },
    { label: 'Published', value: relativeTime(skill.created_at) },
  ];

  const exampleBlock = hasExample && (
    <div className="rounded-card bg-canvas-inset px-6 py-5">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">Example</div>
      <p className="m-0 text-[14px] leading-[22px] text-ink">{skill.example_input || 'No input was recorded.'}</p>
      {showExample && skill.example_output && (
        <div className="mt-4 border-t border-line pt-4">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">What comes back</div>
          <p className="m-0 text-[14px] leading-[22px] text-ink">{skill.example_output}</p>
        </div>
      )}
      {showExample && !skill.example_output && (
        <p className="m-0 mt-3 text-[13px] text-ink-dim">No example output was recorded.</p>
      )}
    </div>
  );

  const improveCard = (
    <Card pad="md">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">Improve this Quick Win</div>
        <Chip tone="ok">Published</Chip>
      </div>
      <div className="mt-2 text-[20px] font-bold leading-[26px] tracking-[-0.01em] text-ink">Released version</div>
      <p className="m-0 mt-2 text-[13px] leading-[21px] text-ink-dim">
        Tell the owner what happened, or request a change. The released version stays unchanged during review.
      </p>
      {mode === 'read' && (
        <div className="mt-4 flex flex-wrap gap-3">
          <Action size="lg" variant="quiet" onClick={() => setMode('feedback')}>Send feedback</Action>
          <Action size="lg" variant="quiet" onClick={() => setMode('customization')}>Request customization</Action>
        </div>
      )}
      {mode !== 'read' && (
        <div className="mt-4">
          <div className="text-[13px] font-semibold text-ink">{FORM[mode].title}</div>
          <p className="m-0 mt-1 text-[12.5px] leading-[20px] text-ink-dim">{FORM[mode].hint}</p>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={FORM[mode].placeholder} className={`${FIELD} mt-3`} />
          <div className="mt-3 flex flex-wrap gap-3">
            <Action size="lg" variant="ghost" onClick={() => { setMode('read'); setNote(''); }}>Back</Action>
            <Action size="lg" onClick={send} disabled={busy} reason="Sending">{FORM[mode].action}</Action>
          </div>
        </div>
      )}
      {done && <p className="m-0 mt-4 text-[12.5px] leading-[20px] text-ink-dim">{sentLine(done)}</p>}
    </Card>
  );

  const configNote = (
    <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">
      Skill configuration, prompts and model settings are managed by the AI Champion. Usage is reported in aggregate under Results; individual runs stay private.
    </p>
  );

  const runActions = (
    <>
      {!running && (
        <Action size="lg" variant="quiet" disabled={!hasExample} reason="No example was recorded for this Quick Win" onClick={() => setShowExample((v) => !v)}>
          {showExample ? 'Hide the example' : 'See an example'}
        </Action>
      )}
      {!running && canRun && <Action size="lg" onClick={() => setRunning(true)}>Run it here</Action>}
      {running && <Action size="lg" variant="quiet" onClick={() => setRunning(false)}>Back to the detail</Action>}
    </>
  );

  if (page) {
    return (
      <RecordPage
        backLabel={page.backLabel}
        onBack={onClose}
        kicker={<Kicker>Quick Win</Kicker>}
        badge={<StatusChip status="ready_short" />}
        title={skill.name}
        sub={skill.problem || 'A company capability published for the business.'}
        actions={runActions}
        aside={(
          <RailCard title="At a glance">
            <div className="[&>div>div]:px-5"><FactList facts={facts} /></div>
            <div className="border-t border-line-soft px-5 py-3.5 text-[12.5px] font-semibold">
              {inAudience && <span className="text-ink-dim">You are in the approved audience</span>}
              {!inAudience && <span className="text-amber-700">Oversight view · not in the approved audience</span>}
            </div>
          </RailCard>
        )}
      >
        {running && <Card pad="md"><SkillRunBody skillId={skill.id} /></Card>}
        {!running && (
          <>
            {exampleBlock}
            {improveCard}
            {configNote}
          </>
        )}
      </RecordPage>
    );
  }

  return (
    <RecordModal
      onClose={onClose}
      kicker={<Kicker>Quick Win</Kicker>}
      badge={<StatusChip status="ready_short" />}
      title={skill.name}
      sub={skill.problem || 'A company capability published for the business.'}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          {inAudience && <span className="text-[13.5px] font-semibold text-ink-dim">You are in the approved audience</span>}
          {!inAudience && <span className="text-[13.5px] font-semibold text-amber-700">Oversight view · not in the approved audience</span>}
          <div className="flex flex-wrap gap-3">{runActions}</div>
        </div>
      )}
    >
      {running && <SkillRunBody skillId={skill.id} />}
      {!running && (
        <>
          <div className="grid overflow-hidden rounded-card border border-line sm:grid-cols-2">
            {facts.map((f) => <Fact key={f.label} label={f.label} value={f.value} />)}
          </div>
          {exampleBlock && <div className="mt-5">{exampleBlock}</div>}
          <div className="mt-5">{improveCard}</div>
          <div className="mt-5">{configNote}</div>
        </>
      )}
    </RecordModal>
  );
};

/* /projects/quick-wins/:id — one Quick Win as its own page. */
export const QuickWinPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { back, label } = useRecordBack('/projects?tab=quick_wins', 'Back to Quick Wins');
  const [skill, setSkill] = useState<AaiSkill | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setSkill(null);
    setFailed(false);
    // The Quick Wins tab lists these, and the single read refuses some it
    // lists, so the page reads the same list and picks this one from it.
    aaiApi.listSkills()
      .then((res) => {
        if (!live) return;
        const found = res.data.skills.find((s) => s.id === id) || null;
        setSkill(found);
        if (!found) setFailed(true);
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [id]);

  if (skill) return <CapabilityDrawer skill={skill} onClose={back} page={{ backLabel: label }} />;
  return (
    <RecordPage backLabel={label} onBack={back} kicker={<Kicker>Quick Win</Kicker>} title="Quick Win">
      {failed && <EmptyState title="This Quick Win is not available" reason="It may have been removed, or it is not shared with you." />}
      {!failed && <Loading label="Opening the Quick Win" />}
    </RecordPage>
  );
};
